import React, { useState, useRef, useEffect } from 'react';
import { useMediaQuery } from '../hooks/useIntersectionObserver';

const APPLE_THEME = {
  white: '#ffffff',
  black: '#1d1d1f',
  textPrimary: '#1d1d1f',
  textSecondary: '#86868b',
  lightGray: '#f5f5f7',
  backgroundGray: '#fbfbfd',
  borderLight: '#d2d2d7',
  appleBlue: '#0066cc',
  systemBlue: '#007aff',
  accent: '#06c',
  cardBackground: 'rgba(255, 255, 255, 0.8)',
  cardBorder: 'rgba(0, 0, 0, 0.04)'
};

const TYPOGRAPHY = {
  display: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
  text: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
};

const useIntersectionObserver = (threshold = 0.1, rootMargin = '0px') => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold,
        rootMargin,
        trackVisibility: true,
        delay: 100
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, rootMargin]);

  return [ref, isVisible];
};

 
const Icon = {
  screen: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="14" rx="2" ry="2"/>
      <line x1="8" y1="21" x2="16" y2="21"/>
    </svg>
  ),
  battery: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="18" height="10" rx="2"/>
      <line x1="22" y1="11" x2="22" y2="13"/>
    </svg>
  ),
  software: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/>
      <rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/>
      <rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  port: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="9" width="16" height="6" rx="2"/>
      <line x1="8" y1="12" x2="10" y2="12"/>
      <line x1="14" y1="12" x2="16" y2="12"/>
    </svg>
  ),
  board: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <circle cx="12" cy="12" r="3"/>
      <line x1="12" y1="3" x2="12" y2="6"/>
      <line x1="12" y1="18" x2="12" y2="21"/>
      <line x1="3" y1="12" x2="6" y2="12"/>
      <line x1="18" y1="12" x2="21" y2="12"/>
    </svg>
  ),
  enterprise: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21V5a2 2 0 0 1 2-2h6v18H3z"/>
      <path d="M11 7h6a2 2 0 0 1 2 2v12h-8V7z"/>
      <line x1="7" y1="8" x2="9" y2="8"/>
      <line x1="7" y1="12" x2="9" y2="12"/>
      <line x1="15" y1="11" x2="17" y2="11"/>
    </svg>
  )
};

const CategoryBlock = ({ title, items, index }) => {
  const [ref, isVisible] = useIntersectionObserver(0.1, '-40px');
  return (
    <div
      ref={ref}
      style={{
        background: APPLE_THEME.white,
        border: `1px solid ${APPLE_THEME.cardBorder}`,
        borderRadius: '18px',
        padding: '28px 24px',
        boxShadow: '0 8px 20px rgba(0,0,0,0.06)',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: `all 0.6s ease ${index * 80}ms`
      }}
    >
      <h3 style={{
        fontFamily: TYPOGRAPHY.display,
        color: APPLE_THEME.textPrimary,
        fontSize: '24px',
        fontWeight: 600,
        marginBottom: '18px'
      }}>{title}</h3>
  <ul className="tiles" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {items.map((it, i) => (
          <li key={i} className="tile" style={{
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 12px',
    borderRadius: '12px',
    cursor: 'pointer',
    color: APPLE_THEME.textPrimary,
    flex: '1 1 140px',
    minWidth: '140px'
          }}
          onClick={() => window.location.assign(`/hizmetler/${it.slug}`)}
          onKeyDown={(e) => { if (e.key === 'Enter') window.location.assign(`/hizmetler/${it.slug}`); }}
          role="button" tabIndex={0}
          >
            <span style={{ color: APPLE_THEME.textPrimary }}>{it.icon}</span>
            <span style={{
              fontFamily: TYPOGRAPHY.text,
              fontSize: '16px'
            }}>{it.label}</span>
            <span style={{ marginLeft: 'auto', color: APPLE_THEME.accent, display: 'flex', alignItems: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M8 4L16 12L8 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const InfoSection = ({ info, index, isReversed = false }) => {
  const [ref, isVisible] = useIntersectionObserver(0.1);
  const isWide = useMediaQuery('768px');

  const textOrder = isWide ? (isReversed ? 2 : 1) : 2;
  const imageOrder = isWide ? (isReversed ? 1 : 2) : 1;

  return (
    <div
      ref={ref}
      style={{
        display: 'grid',
        gridTemplateColumns: isWide ? '1fr 1fr' : '1fr',
        gap: isWide ? '60px' : '20px',
        alignItems: 'center',
        marginBottom: isWide ? '120px' : '60px',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transition: 'all 0.8s ease-out'
      }}
    >
      <div style={{ order: textOrder }}>
        <h2
          style={{
            color: APPLE_THEME.textPrimary,
            fontFamily: TYPOGRAPHY.display,
            fontSize: 'clamp(32px, 4vw, 48px)',
            fontWeight: '600',
            lineHeight: '1.05',
            marginBottom: '20px',
            letterSpacing: '-0.015em'
          }}
        >
          {info.title}
        </h2>
        <p
          style={{
            color: APPLE_THEME.textSecondary,
            fontFamily: TYPOGRAPHY.text,
            fontSize: '21px',
            lineHeight: '1.5',
            marginBottom: '32px',
            fontWeight: '400'
          }}
        >
          {info.description}
        </p>
 
        <div style={{ marginBottom: '32px' }}>
          {info.features.map((feature, idx) => (
            <div
              key={idx}
              style={{
                marginBottom: '16px',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
                transition: `all 0.6s ease ${(idx * 100) + 200}ms`
              }}
            >
              <h4
                style={{
                  color: APPLE_THEME.textPrimary,
                  fontFamily: TYPOGRAPHY.display,
                  fontSize: '19px',
                  fontWeight: '600',
                  marginBottom: '4px'
                }}
              >
                {feature.title}
              </h4>
              <p
                style={{
                  color: APPLE_THEME.textSecondary,
                  fontFamily: TYPOGRAPHY.text,
                  fontSize: '17px',
                  lineHeight: '1.47'
                }}
              >
                {feature.description}
              </p>
            </div>
          ))}
        </div>
 
        <div
          style={{
            color: APPLE_THEME.accent,
            fontFamily: TYPOGRAPHY.text,
            fontSize: '17px',
            fontWeight: '400',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer'
          }}
        >
          <span>Daha fazla bilgi</span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            style={{ marginLeft: '6px' }}
          >
            <path
              d="M8 4L16 12L8 20"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
 
      <div
        style={{
          order: imageOrder,
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'scale(1)' : 'scale(0.95)',
          transition: 'all 0.8s ease-out 0.2s'
        }}
      >
        <div
          style={{
            width: '100%',
            height: isWide ? '400px' : '240px',
            backgroundImage: `url("${info.image}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: '18px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)'
          }}
        />
      </div>
    </div>
  );
};

const Services = () => {
  const [headerRef, headerVisible] = useIntersectionObserver(0.1);

  const categories = [
    {
      title: 'iPhone Servisi',
      items: [
        { label: 'Ekran Değişimi', slug: 'iphone-ekran', icon: Icon.screen },
        { label: 'Batarya Değişimi', slug: 'iphone-batarya', icon: Icon.battery },
        { label: 'Yazılımsal Sorunlar', slug: 'iphone-yazilim', icon: Icon.software }
      ]
    },
    {
      title: 'iPad Servisi',
      items: [
        { label: 'Ekran Değişimi', slug: 'ipad-ekran', icon: Icon.screen },
        { label: 'Şarj Soketleri', slug: 'ipad-sarj-soketi', icon: Icon.port },
        { label: 'Batarya Değişimi', slug: 'ipad-batarya', icon: Icon.battery }
      ]
    },
    {
      title: 'Mac Servisi',
      items: [
        { label: 'Kurumsal Destek', slug: 'mac-kurumsal-destek', icon: Icon.enterprise },
        { label: 'Anakart Sorunları', slug: 'mac-anakart', icon: Icon.board },
        { label: 'Yazılım', slug: 'mac-yazilim', icon: Icon.software }
      ]
    }
  ];

  const infoSections = [
    {
      title: "Neden C‑Store?",
      description: "iPhone, iPad ve Mac için tek noktadan uçtan uca servis. Kurumsal süreçlere uygun dokümantasyon, şeffaf iletişim ve ölçülebilir hizmet kalitesi sunuyoruz.",
      features: [
        { title: "Sertifikalı Teknisyenler", description: "Alanında uzman, sürekli eğitim alan ekipler tarafından işlem yapılır." },
        { title: "Orijinal Kalite Parçalar", description: "Tedarik zinciri denetimli, marka standartlarıyla uyumlu parçalar kullanılır." },
        { title: "Hızlı ve Planlı Süreç", description: "Çoğu işlem aynı gün; durum bilgilendirmeleri düzenli olarak paylaşılır." }
      ],
      image: "/assets/images/products/telefon-tamir.jpeg"
    },
    {
      title: "Kalite Standartları",
      description: "Her onarım öncesi-sonrası 15 adımlı kontrol listesini uygular, cihazı fonksiyon, performans ve kozmetik açıdan test ederiz.",
      features: [
        { title: "Prosedür ve İzlenebilirlik", description: "İşlem kartı, seri numarası ve parça kayıtları saklanır." },
        { title: "Garanti Kapsamı", description: "Parça ve işçilik için yasal çerçevede garanti sunulur." },
        { title: "Müşteri Geri Bildirimi", description: "Sürekli iyileştirme için NPS/CSAT takip edilir." }
      ],
      image: "/assets/images/products/tablet-tamiri.jpg"
    },
    {
      title: "Gizlilik ve Veri Güvenliği",
      description: "KVKK’ya tam uyumla çalışırız. İşlem öncesi yedekleme, işlem sırasında erişim kısıtları ve işlem sonrası silme politikaları uygularız.",
      features: [
        { title: "KVKK Uyumlu Süreç", description: "Veri erişimi sadece yetkili personelle, kayıt altındadır." },
        { title: "Yedekleme ve Geri Yükleme", description: "Talebinize göre profesyonel yedekleme/geri yükleme yapılır." },
        { title: "Gizlilik Taahhüdü", description: "Kişisel içerikleriniz sizin onayınız olmadan erişilmez/paylaşılmaz." }
      ],
      image: "/assets/images/products/ekran-degisimi.jpg"
    }
  ];

  return (
    <section
      id="services"
      style={{
        background: APPLE_THEME.white,
  paddingTop: '40px',
  paddingBottom: '72px'
      }}
    >
      <div style={{ maxWidth: '980px', margin: '0 auto', padding: '0 20px' }}>
    <div
          ref={headerRef}
          style={{
            textAlign: 'center',
      marginBottom: '60px',
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s ease-out'
          }}
        >
          <h1
            style={{
              color: APPLE_THEME.textPrimary,
              fontFamily: TYPOGRAPHY.display,
              fontSize: 'clamp(48px, 6vw, 64px)',
              fontWeight: '600',
              lineHeight: '1.05',
              letterSpacing: '-0.015em',
              marginBottom: '16px'
            }}
          >
            C-Store Hizmetler
          </h1>
          <p
            style={{
              color: APPLE_THEME.textSecondary,
              fontFamily: TYPOGRAPHY.text,
              fontSize: '21px',
              lineHeight: '1.5',
              fontWeight: '400',
              maxWidth: '600px',
              margin: '0 auto 32px auto'
            }}
          >
            Premium kalite, uygun fiyat garantisi ile profesyonel çözümler.
          </p>
          <p
            style={{
              color: APPLE_THEME.textSecondary,
              fontFamily: TYPOGRAPHY.text,
              fontSize: '17px',
              lineHeight: '1.5',
              fontWeight: '400'
            }}
          >
            iPhone, iPad ve Mac için onarım, yazılım ve kurumsal destek hizmetleri.
          </p>
        </div>
 
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px',
            marginBottom: '100px'
          }}
        >
          {categories.map((cat, i) => (
            <CategoryBlock key={i} title={cat.title} items={cat.items} index={i} />
          ))}
        </div>
 
        <div>
          {infoSections.map((info, index) => (
            <InfoSection
              key={index}
              info={info}
              index={index}
              isReversed={index % 2 !== 0}
            />
          ))}
        </div>
 
      </div>
    </section>
  );
};

export default Services;